<template>
    <section v-show="hasDiscoverContent" ref="discoverRegion" :aria-label="t('coachingSetup.home.recommendationsLabel')" tabindex="-1">
        <Transition name="fade" mode="out-in" @after-enter="focusRestoredContent" @after-leave="!showDiscoverRow && !showMyWorkBanner && focusRestoredContent()">
            <GuidedSetupHomePromotion
                v-if="showPromotionBanner && guidedSetupCard && guidedSetupHomePromotion"
                :key="guidedSetupHomePromotion.interaction_count"
                :card="guidedSetupCard"
                :promotion="guidedSetupHomePromotion"
                :navigation-pending="setupNavigationPending"
                :navigation-failed="setupNavigationFailed"
                @retire="handlePromotionRetired"
                @continue="(url, interactionCount) => emit('setup-continue', url, interactionCount)" />
            <HomeMyWorkInsightCarousel v-else-if="showMyWorkBanner" :insights="homepageMyworkInsights" :initial-feed="homepageMyworkFeed" @retire="myWorkRetired = true" />
            <div v-else-if="showDiscoverRow">
                <HomeSectionHeader title="How can I help you today?" />
                <div class="flex flex-col lg:flex-row gap-4">
                    <Transition name="fade" mode="out-in" @after-enter="focusRestoredContent" @after-leave="!companion && focusRestoredContent()">
                        <GuidedSetupHomePromotion
                            v-if="companion === 'setup_promotion' && guidedSetupCard && guidedSetupHomePromotion"
                            :key="`promotion-${guidedSetupHomePromotion.interaction_count}`"
                            class="flex-1 lg:max-w-80"
                            :card="guidedSetupCard"
                            :promotion="guidedSetupHomePromotion"
                            :navigation-pending="setupNavigationPending"
                            :navigation-failed="setupNavigationFailed"
                            @retire="handlePromotionRetired"
                            @continue="(url, interactionCount) => emit('setup-continue', url, interactionCount)" />
                        <MyWorkActivationCard
                            v-else-if="companion === 'my_work' && myworkCard"
                            class="flex-1 min-w-60 lg:max-w-72"
                            :card-state="myworkCard.state"
                            :card-info="myworkCard.insight" />
                        <ProfileCompletionCard
                            v-else-if="companion === 'profile_completion' && profileCompletionCard"
                            class="flex-1 min-w-60 lg:max-w-72"
                            :card-sections="profileCompletionCard.sections" />
                        <ManagerSelectorCard v-else-if="companion === 'manager'" class="flex-1 min-w-60 lg:max-w-72" @success="handleManagerSelectionSuccess" />
                        <GuidedSetupHomeCard v-else-if="companion === 'setup_legacy' && guidedSetupCard" class="flex-1 min-w-60 lg:max-w-[310px]" :card="guidedSetupCard" />
                        <PerspectivesLinkCard
                            v-else-if="companion === 'perspective'"
                            class="flex-1 min-w-60"
                            :class="hasPerspectiveResult ? 'lg:max-w-72' : 'lg:max-w-[310px]'"
                            :perspective-report="perspectiveReport"
                            :is-fetching="isFetching"
                            @refresh="refresh" />
                        <CollaborationProfileLinkCard
                            v-else-if="companion === 'collaboration_profile'"
                            class="flex-1 min-w-60 lg:max-w-72"
                            :has-perspective-result="hasPerspectiveResult" />
                    </Transition>
                    <DiscoverUseCasesDisplay
                        v-if="shouldShowDiscoverEntrances"
                        class="flex-1"
                        :show-title="false"
                        :use-cases="useCases.cases"
                        :location="ChatUseCaseLocation.DISCOVER_HOMEPAGE"
                        @insight-hover="(...args) => $emit('insight-hover', ...args)"
                        @insight-hover-end="(...args) => $emit('insight-hover-end', ...args)"
                        @insight-clicked="(...args) => $emit('insight-clicked', ...args)" />
                </div>
            </div>
        </Transition>
    </section>
</template>

<script setup lang="ts">
import { router, usePage } from "@inertiajs/vue3";
import type { PerspectiveReport, UserDetails } from "~vue/appTypes";
import CollaborationProfileLinkCard from "~vue/ChatHome/CollaborationProfileLinkCard.vue";
import GuidedSetupHomeCard from "~vue/ChatHome/GuidedSetupHomeCard.vue";
import GuidedSetupHomePromotion from "~vue/ChatHome/GuidedSetupHomePromotion.vue";
import HomeMyWorkInsightCarousel from "~vue/ChatHome/HomeMyWorkInsightCarousel.vue";
import HomeSectionHeader from "~vue/ChatHome/HomeSectionHeader.vue";
import ManagerSelectorCard from "~vue/ChatHome/ManagerSelectorCard.vue";
import MyWorkActivationCard from "~vue/ChatHome/MyWorkActivationCard.vue";
import PerspectivesLinkCard from "~vue/ChatHome/PerspectivesLinkCard.vue";
import ProfileCompletionCard from "~vue/ChatHome/ProfileCompletionCard.vue";
import DiscoverUseCasesDisplay from "~vue/ChatUseCaseEntrance/DiscoverUseCasesDisplay.vue";
import { ChatUseCaseLocation } from "~vue/ChatUseCaseEntrance/utils";
import { usePerspectiveReportImport } from "~vue/composables/usePerspectiveReportImport";
import type { GuidedSetupHomePromotion as GuidedSetupHomePromotionData, SetupHomeCard } from "~vue/ProfileSetup/types";
import type { ChatEntranceUseCaseData } from "~vue/types/chat";
import type { ChatEntranceUseCasesResponse, HomepageMyWorkInsight, MyWorkCardData, ProfileCompletionSummary } from "~vue/types/chat_home";
import type { MyWorkInsightFeed } from "~vue/types/labs";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, type Ref, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
    defineProps<{
        useCases?: ChatEntranceUseCasesResponse;
        perspectiveReportEnabled?: boolean;
        perspectiveReport?: PerspectiveReport | null;
        perspectiveAssessmentEnabled?: boolean;
        perspectiveCardDismissed?: boolean;
        myworkCard?: MyWorkCardData | null;
        profileCompletionCard?: ProfileCompletionSummary | null;
        /** `null` unless the coaching setup is offered to this user and something is still outstanding. */
        guidedSetupCard?: SetupHomeCard | null;
        guidedSetupHomePromotion?: GuidedSetupHomePromotionData | null;
        setupNavigationPending?: boolean;
        setupNavigationFailed?: boolean;
        collaborationProfileEnabled?: boolean;
        hasCollaborationProfile?: boolean;
        homepageMyworkInsightsEnabled?: boolean;
        homepageMyworkInsights?: HomepageMyWorkInsight[];
        homepageMyworkFeed?: MyWorkInsightFeed | null;
    }>(),
    {
        useCases: () => ({ enabled: false, cases: [] }),
        perspectiveReportEnabled: false,
        perspectiveReport: null,
        perspectiveAssessmentEnabled: false,
        perspectiveCardDismissed: false,
        myworkCard: null,
        profileCompletionCard: null,
        guidedSetupCard: null,
        guidedSetupHomePromotion: null,
        setupNavigationPending: false,
        setupNavigationFailed: false,
        collaborationProfileEnabled: false,
        hasCollaborationProfile: false,
        homepageMyworkInsightsEnabled: false,
        homepageMyworkInsights: () => [],
        homepageMyworkFeed: null,
    },
);

const emit = defineEmits<{
    "insight-clicked": [useCase: ChatEntranceUseCaseData];
    "insight-hover": [useCase: ChatEntranceUseCaseData];
    "insight-hover-end": [];
    "manager-selection-success": [value: string];
    "setup-continue": [url: string, interactionCount: number];
    "setup-retired": [interactionCount: number];
}>();

const userDetails = inject<Ref<UserDetails>>("user_details");
const { t } = useI18n();
const discoverRegion = useTemplateRef<HTMLElement>("discoverRegion");
const restorePromotionFocus = ref(false);

const page = usePage();

// The Team Tools import polling lives here (not in a card) so it runs regardless of which
// home-page card wins priority. `perspectiveReport` reflects the latest imported report.
const { perspectiveReport, refresh, isFetching } = usePerspectiveReportImport({
    reportEnabled: () => props.perspectiveReportEnabled,
    assessmentEnabled: () => props.perspectiveAssessmentEnabled,
    initialReport: props.perspectiveReport,
    onReportFound: () => router.reload({ only: ["perspective_archetype"] }),
});

const perspectiveEnabled = computed(() => props.perspectiveReportEnabled || props.perspectiveAssessmentEnabled);

const hasPerspectiveResult = computed(() => !!page.props.perspective_assessment_completed || !!page.props.perspective_archetype || !!perspectiveReport.value);

const shouldShowDiscoverEntrances = computed(() => props.useCases.enabled && props.useCases.cases.length > 0);
const consumedInteractionCount = ref<number | null>(null);
const myWorkRetired = ref(false);
const promotionDue = computed(() => {
    const card = props.guidedSetupCard;
    const promotion = props.guidedSetupHomePromotion;
    return !!card && card.completed_count < card.total && !!promotion?.is_due && promotion.interaction_count !== consumedInteractionCount.value;
});
const showPromotionBanner = computed(() => promotionDue.value && props.guidedSetupHomePromotion?.group === "treatment" && props.guidedSetupHomePromotion.presentation === "banner");
const showMyWorkBanner = computed(() => !promotionDue.value && props.homepageMyworkInsightsEnabled && !myWorkRetired.value);
const companion = computed(() => {
    if (promotionDue.value) {
        return "setup_promotion";
    }
    if (props.myworkCard) {
        return "my_work";
    }
    if (props.profileCompletionCard) {
        return "profile_completion";
    }
    if (userDetails?.value.is_manager === null) {
        return "manager";
    }
    let perspective: "setup_legacy" | "perspective" | null = null;
    if (perspectiveEnabled.value) {
        if (props.guidedSetupHomePromotion === null && props.guidedSetupCard) {
            perspective = "setup_legacy";
        } else if (!props.perspectiveCardDismissed) {
            perspective = "perspective";
        }
    }
    if (perspective && !hasPerspectiveResult.value) {
        return perspective;
    }
    if (props.collaborationProfileEnabled && !props.hasCollaborationProfile) {
        return "collaboration_profile";
    }
    return perspective;
});
const showDiscoverRow = computed(() => !showPromotionBanner.value && !showMyWorkBanner.value && (!!companion.value || shouldShowDiscoverEntrances.value));
const hasDiscoverContent = computed(() => showPromotionBanner.value || showMyWorkBanner.value || showDiscoverRow.value);

const handleManagerSelectionSuccess = (value: string) => {
    logUserInteraction(AmplitudeEventNames.MANAGER_SELECTOR_CONFIRMED, { selection: value });
    if (userDetails) {
        userDetails.value.is_manager = value === "yes";
    }
    emit("manager-selection-success", value);
};

function handlePromotionRetired(interactionCount: number, restoreFocus: boolean): void {
    restorePromotionFocus.value = restoreFocus;
    consumedInteractionCount.value = interactionCount;
    emit("setup-retired", interactionCount);
}

function focusRestoredContent(): void {
    if (restorePromotionFocus.value) {
        const target = hasDiscoverContent.value ? discoverRegion.value : discoverRegion.value?.closest<HTMLElement>("main");
        target?.focus();
        restorePromotionFocus.value = false;
    }
}
</script>

<style scoped>
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}
</style>
